import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
    Plus,
    Pencil,
    GraduationCap,
    FileText,
    Briefcase,
    Users,
    AlertTriangle,
    CheckCircle,
    Loader2,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import EducationModal from "./modals/EducationModal";
import ExamModal from "./modals/ExamModal";
import ExperienceModal from "./modals/ExperienceModal";
import ReferenceModal from "./modals/ReferenceModal";
import { saveEducationalHistory, getEducationalHistory, syncCvFromSalesforce, getCvRequirements, deleteEducationalHistoryRecord } from "@/services/universityService";
import type { EducationalHistorySection, CvRequirement, CvFieldMapping } from "@/services/universityService";

const initialEducationList: any[] = [];
const initialExamList: any[] = [];
const initialExperienceList: any[] = [];
const initialReferenceList: any[] = [];

// ---------------------------------------------------------------------------
// Dynamic requirement evaluation engine.
// Mirrors the Salesforce DynamicSettingSearch / criteria_logic evaluation.
// ---------------------------------------------------------------------------

function compareValues(actual: string | null, operator: string, expected: string): boolean {
    if (operator === "IsNull") return actual === null || actual === undefined;
    if (operator === "IsEmpty") return actual === null || actual === undefined || actual === "";
    if (actual === null || actual === undefined) return false;
    if (operator === "==") return actual.trim().toLowerCase() === expected.trim().toLowerCase();
    if (operator === "!=") return actual.trim().toLowerCase() !== expected.trim().toLowerCase();
    const a = parseFloat(actual);
    const e = parseFloat(expected);
    if (!isNaN(a) && !isNaN(e)) {
        if (operator === ">") return a > e;
        if (operator === "<") return a < e;
        if (operator === ">=") return a >= e;
        if (operator === "<=") return a <= e;
    }
    return false;
}

function applyCriteriaLogic(logic: string, results: Record<number, boolean>): boolean {
    if (!logic.trim()) return Object.values(results).every(Boolean);

    
    const substituted = logic
        .replace(/\bAND\b/g, "&&")
        .replace(/\bOR\b/g, "||")
        .replace(/\bNOT\b/g, "!")
        .replace(/\b(\d+)\b/g, (_m, n) => (results[parseInt(n)] ? "true" : "false"));

    const tokens = substituted.match(/true|false|&&|\|\||!|\(|\)/g) ?? [];
    let pos = 0;

    function parseOr(): boolean {
        let left = parseAnd();
        while (tokens[pos] === "||") { pos++; left = left || parseAnd(); }
        return left;
    }
    function parseAnd(): boolean {
        let left = parseNot();
        while (tokens[pos] === "&&") { pos++; left = left && parseNot(); }
        return left;
    }
    function parseNot(): boolean {
        if (tokens[pos] === "!") { pos++; return !parseNot(); }
        return parsePrimary();
    }
    function parsePrimary(): boolean {
        if (tokens[pos] === "(") {
            pos++;
            const value = parseOr();
            pos++; // consume ")"
            return value;
        }
        return tokens[pos++] === "true";
    }

    try {
        return parseOr();
    } catch {
        return Object.values(results).every(Boolean);
    }
}

function getRecordFieldValue(record: any, fieldApiName: string, fieldMappings: Record<string, CvFieldMapping>): string | null {
    const mapping = fieldMappings[fieldApiName];
    const localField = mapping?.local_field ?? fieldApiName;
    const val = record[localField];
    if (val === null || val === undefined) return null;
    return String(val);
}

function evaluateCriteriaAgainstRecords(
    criteria: CvRequirement["criteria"],
    criteriaLogic: string,
    records: any[],
    fieldMappings: Record<string, CvFieldMapping>,
): boolean {
    if (records.length === 0) return false;
    // At least one record must satisfy the full criteria logic
    return records.some((record) => {
        const results: Record<number, boolean> = {};
        for (const c of criteria) {
            const actual = getRecordFieldValue(record, c.field_api_name, fieldMappings);
            results[c.sort_order] = compareValues(actual, c.operator, c.field_value);
        }
        return applyCriteriaLogic(criteriaLogic, results);
    });
}

function getRecordsForSection(
    sectionType: string,
    cvRecords: any[],
    examRecords: any[],
    expRecords: any[],
    refRecords: any[],
): any[] {
    if (sectionType === "Educational_Information") return cvRecords;
    if (sectionType === "Proficiency_Exam") return examRecords;
    if (sectionType === "Job_Information") return expRecords;
    if (sectionType === "Reference") return refRecords;
    return [];
}

function validateRequirements(
    cvRecords: any[],
    examRecords: any[],
    expRecords: any[],
    refRecords: any[],
    requirements: CvRequirement[],
    fieldMappings: Record<string, CvFieldMapping>,
) {
    
    return requirements.map((req) => {
        const sectionRecords = getRecordsForSection(req.result, cvRecords, examRecords, expRecords, refRecords);
        const hasRecords = sectionRecords.length > 0;

        if (req.result === "Reference" && req.required_count !== null) {
            const needed = req.required_count;
            const have = sectionRecords.length;
            const result: "validate" | "success" | "failed" =
                have >= needed ? "success" : "validate";
            return {
                id: String(req.id),
                sectionTitle: req.name,
                message: req.message,
                requirementType: req.result,
                result,
                isRequired: req.is_required,
                requiredCount: needed,
                haveCount: have,
            };
        }

        const validationCriteria = req.criteria.filter((c) => c.is_active);

        let criteriaMet = false;
        if (validationCriteria.length === 0) {
            criteriaMet = hasRecords;
        } else {
            criteriaMet = evaluateCriteriaAgainstRecords(
                validationCriteria,
                req.criteria_logic,
                sectionRecords,
                fieldMappings,
            );
        }

        const result: "validate" | "success" | "failed" =
            !hasRecords ? "validate" : criteriaMet ? "success" : "failed";

        return {
            id: String(req.id),
            sectionTitle: req.name,
            message: req.message,
            requirementType: req.result,
            result,
            isRequired: req.is_required,
            requiredCount: null as number | null,
            haveCount: null as number | null,
        };
    });
}

// CV section visibility settings
interface CvVisibilitySettings {
    showEducation?: boolean;
    showProficiencyExam?: boolean;
    showJobExperience?: boolean;
    showReference?: boolean;
}

export default function EducationalHistory({
    appNumber,
    onRequirementChange,
    cvVisibility = {},
    degrees: degreesProp = [],
    gradingSystems: gradingSystemsProp = [],
    proficiencyExams: proficiencyExamsProp = [],
    jobTypes: jobTypesProp = [],
    hideGpa = false,
}: {
    appNumber: string;
    onRequirementChange?: (met: boolean) => void;
    cvVisibility?: CvVisibilitySettings;
    degrees?: Array<{ label: string; value: string }>;
    gradingSystems?: Array<{ label: string; value: string }>;
    proficiencyExams?: Array<{ label: string; value: string }>;
    jobTypes?: Array<{ label: string; value: string }>;
    hideGpa?: boolean;
}) {
    const [_activeSection, _setActiveSection] = useState<string>("Education");
    const [editingItem, setEditingItem] = useState<any>(null);
    const [selectedCountry, setSelectedCountry] = useState("");
    const [openCountryPicker, setOpenCountryPicker] = useState(false);
    const [selectedDegree, setSelectedDegree] = useState("");
    const [selectedGpaType, setSelectedGpaType] = useState("");

    // Separate modal states
    const [isEduModalOpen, setIsEduModalOpen] = useState(false);
    const [isExamModalOpen, setIsExamModalOpen] = useState(false);
    const [isExpModalOpen, setIsExpModalOpen] = useState(false);
    const [isRefModalOpen, setIsRefModalOpen] = useState(false);

    const [educationList, setEducationList] = useState(initialEducationList);
    const [examList, setExamList] = useState(initialExamList);
    const [experienceList, setExperienceList] = useState(initialExperienceList);
    const [referenceList, setReferenceList] = useState(initialReferenceList);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [savingSection, setSavingSection] = useState<string | null>(null);

    const [cvRequirements, setCvRequirements] = useState<CvRequirement[]>([]);
    const [fieldMappings, setFieldMappings] = useState<Record<string, CvFieldMapping>>({});

    // True while the initial SF inbound sync + DB load is running on mount.
    const [isSyncingFromSf, setIsSyncingFromSf] = useState(true);

    // Countries fetched from Setting__c
    const [countries, setCountries] = useState<{ label: string; value: string }[]>([]);

    // Fetch countries on mount
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await fetch('/api/integrations/hub/countries/');
                const data = await response.json();
                if (data.status === 'success' && data.countries?.length > 0) {
                    setCountries(
                        data.countries.map((name: string) => ({
                            label: name,
                            value: name,
                        }))
                    );
                }
            } catch (error) {
                console.error('Failed to fetch countries:', error);
            }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        if (!appNumber) return;
        getCvRequirements(appNumber)
            .then((res) => {
                if (res.status === 'success') {
                    setCvRequirements(res.requirements);
                    setFieldMappings(res.field_mappings);
                }
            })
            .catch(() => {});
    }, [appNumber]);

    // Default to true if not specified (show all sections by default)
    const showEducation = cvVisibility.showEducation !== false;
    const showProficiencyExam = cvVisibility.showProficiencyExam !== false;
    const showJobExperience = cvVisibility.showJobExperience !== false;
    const showReference = cvVisibility.showReference !== false;

    const allSections = [
        {
            id: "edu",
            title: "Education",
            icon: GraduationCap,
            items: educationList,
            open: () => setIsEduModalOpen(true),
            visible: showEducation,
        },
        {
            id: "exam",
            title: "Exams",
            icon: FileText,
            items: examList,
            open: () => setIsExamModalOpen(true),
            visible: showProficiencyExam,
        },
        {
            id: "exp",
            title: "Experience",
            icon: Briefcase,
            items: experienceList,
            open: () => setIsExpModalOpen(true),
            visible: showJobExperience,
        },
        {
            id: "ref",
            title: "References",
            icon: Users,
            items: referenceList,
            open: () => setIsRefModalOpen(true),
            visible: showReference,
        },
    ];

    // Filter sections based on visibility settings
    const sections = allSections.filter(section => section.visible);

    const handleSaveEducation = (data: any) =>
        _handleSave('education', data, educationList, setEducationList, () => setIsEduModalOpen(false));

    const _handleSave = async (
        section: EducationalHistorySection,
        data: any,
        list: any[],
        setList: (v: any[]) => void,
        closeModal: () => void,
    ) => {
        if (!appNumber) return;
        setSaveError(null);
        setSavingSection(section);
        const tempId = data.id ?? Date.now();
        
        const existingRecord = data.id ? list.find((item) => item.id === data.id) : undefined;
        const recordToSend = data.id
            ? { ...(existingRecord ?? {}), ...data }
            : { ...data, id: tempId };
        try {
            const res = await saveEducationalHistory(appNumber, section, [recordToSend] as any);
            if (res.status === 'error') {
                setSaveError(res.detail || 'Failed to save. Please try again.');
                throw new Error(res.detail || 'Failed to save.');
            }
            const savedEntry = res.saved?.[0];
            const withIds = savedEntry
                ? {
                    ...recordToSend,
                    id: savedEntry.db_id,
                    ...(savedEntry.salesforce_id ? { salesforce_id: savedEntry.salesforce_id } : {}),
                  }
                : recordToSend;
            const updated = data.id
                ? list.map((item) => item.id === data.id ? withIds : item)
                : [...list, withIds];
            setList(updated);
            closeModal();
            setEditingItem(null);
        } catch (e: any) {
            const msg = e?.response?.data?.detail || e?.message || 'Failed to save. Please try again.';
            setSaveError(msg);
            throw e;
        } finally {
            setSavingSection(null);
        }
    };

    const handleSaveExam = (data: any) =>
        _handleSave('exam', data, examList, setExamList, () => setIsExamModalOpen(false));

    const handleSaveExperience = (data: any) =>
        _handleSave('experience', data, experienceList, setExperienceList, () => setIsExpModalOpen(false));

    const handleSaveReference = (data: any) =>
        _handleSave('reference', data, referenceList, setReferenceList, () => setIsRefModalOpen(false));

    const handleOpenAdd = (sectionId: string) => {
        setEditingItem(null);
        setSelectedCountry("");
        setSelectedDegree("");
        setSelectedGpaType("");
        if (sectionId === "edu") setIsEduModalOpen(true);
        if (sectionId === "exam") setIsExamModalOpen(true);
        if (sectionId === "exp") setIsExpModalOpen(true);
        if (sectionId === "ref") setIsRefModalOpen(true);
    };

    const handleEdit = (item: any, sectionId: string) => {
        setEditingItem(item);
        if (sectionId === "edu") {
            setSelectedCountry(item.country?.toLowerCase() || "");
            setSelectedDegree(item.degree || "");
            setSelectedGpaType(item.gpaType || "");
            setIsEduModalOpen(true);
        }
        if (sectionId === "exam") setIsExamModalOpen(true);
        if (sectionId === "exp") setIsExpModalOpen(true);
        if (sectionId === "ref") setIsRefModalOpen(true);
    };

    // Delete handlers
    const _handleDelete = async (
        section: EducationalHistorySection,
        list: any[],
        setList: (v: any[]) => void,
        closeModal: () => void,
    ) => {
        if (!editingItem) return;
        const item = editingItem;
        setList(list.filter((i) => i.id !== item.id));
        closeModal();
        setEditingItem(null);
        const dbId = typeof item.id === "number" && item.id < 2_000_000_000 ? item.id : null;
        if (appNumber && dbId) {
            try {
                await deleteEducationalHistoryRecord(appNumber, section, dbId);
            } catch (err) {
                console.error(`Failed to delete ${section} record from DB:`, err);
            }
        }
    };

    const handleDeleteEducation = () =>
        _handleDelete('education', educationList, setEducationList, () => setIsEduModalOpen(false));

    const handleDeleteExam = () =>
        _handleDelete('exam', examList, setExamList, () => setIsExamModalOpen(false));

    const handleDeleteExperience = () =>
        _handleDelete('experience', experienceList, setExperienceList, () => setIsExpModalOpen(false));

    const handleDeleteReference = () =>
        _handleDelete('reference', referenceList, setReferenceList, () => setIsRefModalOpen(false));

    // On mount: trigger SF inbound sync (once per minute, cooldown enforced
    // server-side), then load the current DB state regardless of sync outcome.
    useEffect(() => {
        if (!appNumber) {
            setIsSyncingFromSf(false);
            return;
        }

        setIsSyncingFromSf(true);
        let cancelled = false;
        const loadFromDb = () => {
            getEducationalHistory(appNumber)
                .then((res) => {
                    if (cancelled) return;
                    if (res.status === 'success') {
                        if (res.education?.length)   setEducationList(res.education);
                        if (res.exams?.length)        setExamList(res.exams);
                        if (res.experiences?.length)  setExperienceList(res.experiences);
                        if (res.references?.length)   setReferenceList(res.references);
                    }
                })
                .catch(() => {})
                .finally(() => {
                    if (!cancelled) setIsSyncingFromSf(false);
                });
        };

        syncCvFromSalesforce(appNumber)
            .catch(() => {})
            .finally(() => {
                if (!cancelled) loadFromDb();
            });

        return () => { cancelled = true; };
    }, [appNumber]);

    const requirementMessages = useMemo(
        () => {
            const allRequirements = validateRequirements(
                educationList,
                examList,
                experienceList,
                referenceList,
                cvRequirements,
                fieldMappings,
            );
            // Filter out requirements for hidden sections
            return allRequirements.filter((req) => {
                if (req.requirementType === "Educational_Information") return showEducation;
                if (req.requirementType === "Proficiency_Exam") return showProficiencyExam;
                if (req.requirementType === "Job_Information") return showJobExperience;
                if (req.requirementType === "Reference") return showReference;
                return true;
            });
        },
        [educationList, examList, experienceList, referenceList, cvRequirements, fieldMappings, showEducation, showProficiencyExam, showJobExperience, showReference]
    );

    const requirementMet = requirementMessages
        .filter((r: { isRequired: boolean }) => r.isRequired)
        .every((r: { result: string }) => r.result === "success");

    useEffect(() => {
        onRequirementChange?.(requirementMet);
    }, [requirementMet]);

    const sectionReqMap = useMemo(() => {
        const typeToId: Record<string, string> = {
            Educational_Information: "edu",
            Proficiency_Exam: "exam",
            Job_Information: "exp",
            Reference: "ref",
        };
        const map: Record<string, typeof requirementMessages> = {};
        for (const req of requirementMessages) {
            const sid = typeToId[req.requirementType];
            if (sid) {
                if (!map[sid]) map[sid] = [];
                map[sid].push(req);
            }
        }
        return map;
    }, [requirementMessages]);

    return (
        <div className="space-y-6 pb-10">
            <div className="space-y-2">
                <h2 className="text-2xl md:text-4xl font-serif font-black text-[#001B44] leading-tight text-left">
                    Educational History
                </h2>
                <p className="text-slate-900 mt-2 md:mt-4 text-base md:text-lg font-medium max-w-2xl text-left">
                    Please provide your academic and professional background details.
                </p>
            </div>

            {/* Spinner shown during the initial mount data load */}
            {isSyncingFromSf && (
                <div className="flex justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
            )}

            {/* Legend */}
            {requirementMessages.length > 0 && (
                <div className="flex items-center gap-3 flex-wrap text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    <span className="flex items-center gap-1.5">
                        <span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-500" />
                        Required — must be completed to proceed
                    </span>
                    <span className="text-slate-300">·</span>
                    <span className="flex items-center gap-1.5">
                        <span className="inline-block w-2.5 h-2.5 rounded-sm bg-slate-400" />
                        Optional — improves your application
                    </span>
                </div>
            )}

            {saveError && (
                <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4">
                    <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-black text-red-800 uppercase tracking-wide mb-0.5">Save Failed</p>
                        <p className="text-xs font-medium text-red-700">{saveError}</p>
                    </div>
                </div>
            )}

            <div className="space-y-8">
                {sections.map((section) => {
                    const reqs = sectionReqMap[section.id] ?? [];
                    const hasAnyRequired = reqs.some((r) => r.isRequired);
                    const anyFailed = reqs.some((r) => r.isRequired && r.result === "failed");
                    const anyValidate = reqs.some((r) => r.isRequired && r.result === "validate");
                    const allSuccess = reqs.length > 0 && reqs.every((r) => r.result === "success");

                    // Border & background based on worst status
                    let cardBorder = "border-slate-200";
                    let cardBg = "bg-slate-50/70";
                    if (hasAnyRequired && anyFailed) { cardBorder = "border-amber-300"; cardBg = "bg-amber-50/40"; }
                    else if (hasAnyRequired && anyValidate) { cardBorder = "border-blue-200"; cardBg = "bg-blue-50/30"; }
                    else if (allSuccess) { cardBorder = "border-emerald-200"; cardBg = "bg-emerald-50/30"; }

                    return (
                    <div key={section.id} className="space-y-3">
                        {/* Section header card */}
                        <div className={`${cardBg} border ${cardBorder} rounded-2xl p-4 shadow-sm space-y-3`}>
                            {/* Top row: icon + title + Add button */}
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <div className="w-9 h-9 md:w-12 md:h-12 rounded-xl bg-[#001B44] flex items-center justify-center text-white shadow-md shrink-0">
                                        <section.icon className="w-4 h-4 md:w-6 md:h-6" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="text-base md:text-xl font-black text-slate-900 uppercase tracking-tight">
                                                {section.title}
                                            </h3>
                                            {reqs.length > 0 && (
                                                hasAnyRequired ? (
                                                    <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-white bg-red-500 px-2 py-0.5 rounded-md shrink-0">
                                                        Required
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-slate-500 bg-slate-200 px-2 py-0.5 rounded-md shrink-0">
                                                        Optional
                                                    </span>
                                                )
                                            )}
                                            {allSuccess && (
                                                <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-md shrink-0">
                                                    <CheckCircle className="w-2.5 h-2.5" /> Satisfied
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                                {section.items.length} {section.items.length === 1 ? "Record" : "Records"}
                                            </p>
                                            {reqs.map((req) => {
                                                if (req.requiredCount === null || req.haveCount === null) return null;
                                                return (
                                                    <span key={req.id} className="text-[10px] font-bold text-slate-400">
                                                        · {Math.min(req.haveCount, req.requiredCount)}/{req.requiredCount} required
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleOpenAdd(section.id)}
                                    disabled={savingSection !== null}
                                    className="rounded-xl border-slate-200 hover:border-[#001B44] hover:text-[#001B44] gap-1.5 font-bold text-xs shrink-0"
                                >
                                    <Plus className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{savingSection ? "Saving…" : "Add "}</span>Record
                                </Button>
                            </div>

                            {/* Requirement messages — full width below the header row */}
                            {reqs.filter((req) => req.message).length > 0 && (
                                <div className="space-y-1.5">
                                    {reqs.filter((req) => req.message).map((req) => (
                                        <div key={req.id} className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-xs font-medium leading-relaxed ${
                                            req.result === "success"
                                                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                                : req.isRequired
                                                ? "bg-blue-50 border-blue-200 text-blue-700"
                                                : "bg-slate-100 border-slate-200 text-slate-500"
                                        }`}>
                                            {req.result === "success"
                                                ? <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                                : <span className="shrink-0 leading-none mt-0.5">•</span>
                                            }
                                            <span>{req.message}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 gap-3">
                            {section.items.length === 0 && (
                                <button
                                    onClick={() => handleOpenAdd(section.id)}
                                    disabled={savingSection !== null}
                                    className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-2xl py-6 text-slate-400 hover:border-[#001B44]/30 hover:text-[#001B44] transition-all text-sm font-bold"
                                >
                                    <Plus className="w-4 h-4" /> Add Record
                                </button>
                            )}
                            {section.items.map((item: any) => (
                                <Card
                                    key={item.id}
                                    className="group hover:border-[#001B44]/20 hover:shadow-md transition-all cursor-pointer border-slate-100 shadow-sm rounded-2xl"
                                    onClick={() => handleEdit(item, section.id)}
                                >
                                    <CardContent className="p-4 flex justify-between items-center">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)] shrink-0" />
                                            <div className="min-w-0">
                                                <h4 className="font-bold text-slate-900 text-sm truncate">
                                                    {item.name || item.company || item.role}
                                                </h4>
                                                <p className="text-xs font-medium text-slate-500 truncate">
                                                    {section.id === "edu" &&
                                                        `${item.degree} • ${item.field}`}
                                                    {section.id === "exam" &&
                                                        `${item.type} • Score: ${item.score}`}
                                                    {section.id === "exp" &&
                                                        `${item.role} at ${item.company}`}
                                                    {section.id === "ref" &&
                                                        `${item.jobTitle} • ${item.email}`}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {section.id !== "ref" && (
                                                <span className="hidden sm:inline text-xs font-bold text-slate-400 bg-slate-50 px-2 py-1 rounded-full border border-slate-100">
                                                    {section.id === "edu" && item.degree !== "Secondary School" && item.beginMonth
                                                        ? `${item.beginMonth}/${item.beginYear} - ${item.endMonth}/${item.endYear}`
                                                        : section.id === "exam"
                                                        ? `${item.examMonth}/${item.examYear}`
                                                        : item.year || item.date || item.duration}
                                                </span>
                                            )}
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="text-slate-300 group-hover:text-[#001B44] transition-colors"
                                                onClick={() => handleEdit(item, section.id)}
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                    );
                })}
            </div>

            {/* Education Modal */}
            <EducationModal
                isOpen={isEduModalOpen}
                onOpenChange={setIsEduModalOpen}
                editingItem={editingItem}
                selectedCountry={selectedCountry}
                setSelectedCountry={setSelectedCountry}
                openCountryPicker={openCountryPicker}
                setOpenCountryPicker={setOpenCountryPicker}
                selectedDegree={selectedDegree ?? ""}
                setSelectedDegree={setSelectedDegree}
                selectedGpaType={selectedGpaType}
                setSelectedGpaType={setSelectedGpaType}
                onDelete={handleDeleteEducation}
                onSave={handleSaveEducation}
                gradingSystems={gradingSystemsProp}
                degrees={degreesProp}
                countries={countries}
                hideGpa={hideGpa}
            />

            {/* Exam Modal */}
            <ExamModal
                isOpen={isExamModalOpen}
                onOpenChange={setIsExamModalOpen}
                editingItem={editingItem}
                onDelete={handleDeleteExam}
                onSave={handleSaveExam}
                examTypes={proficiencyExamsProp}
            />

            {/* Experience Modal */}
            <ExperienceModal
                isOpen={isExpModalOpen}
                onOpenChange={setIsExpModalOpen}
                editingItem={editingItem}
                onDelete={handleDeleteExperience}
                onSave={handleSaveExperience}
                countries={countries}
                jobTypes={jobTypesProp}
            />

            {/* Reference Modal */}
            <ReferenceModal
                isOpen={isRefModalOpen}
                onOpenChange={setIsRefModalOpen}
                editingItem={editingItem}
                onDelete={handleDeleteReference}
                onSave={handleSaveReference}
            />
        </div>
    );
}
